/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from 'react'
import {GroupedPackageItem} from 'types/entities/GroupedPackageItem'
import {currencyNumberFormat, getSelectedUpsell, translate} from 'utils/utility'
import {useComputeInitialCount} from 'utils/upsellsUtils'
import {useTypedSelector} from 'helpers/reducers'
import {useAppDispatch} from 'helpers/store'
import BookingAction from 'store/booking/BookingAction'
import {useTranslation} from 'react-i18next'
import {toast} from 'utils/toastUtils'
import UiPill from "components/Ui/UiPill";
import PackageUpsellSelectableRow from 'pages/Front/Booking/components/PackageUpsell/PackageUpsellSelectableRow'
import PackageUpsellSlideOver from 'pages/Front/Booking/components/PackageUpsell/PackageUpsellSlideOver'
import UiRoundedContent from "components/Ui/UiRoundedContent";

interface UpsellPriceListSimpleRowProps {
    item: GroupedPackageItem
    isCompact?: boolean
}

const toCustomTextValue = (value: unknown): string => {
    if (typeof value === 'string') {
        return value
    }

    if (typeof value === 'number') {
        return String(value)
    }

    return ''
}

const UpsellPriceListSimpleRow = (props: UpsellPriceListSimpleRowProps) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const bookingProcess = useTypedSelector((state) => state.bookingState.bookingProcess)
    const bookingFilter = useTypedSelector((state) => state.bookingState.bookingProcess.filter)
    const [isSlideOverOpen, setIsSlideOverOpen] = useState(false)

    const packageItem = props.item.items[0]
    const selectedUpsell = getSelectedUpsell(bookingProcess.upsell, packageItem)
    const isSelected = selectedUpsell !== undefined
    const selectedCount = selectedUpsell?.count ?? 0

    const { computeInitialCount } = useComputeInitialCount(packageItem)

    const competitionUpsell = packageItem.extra?.groupName === 'Competitions'
    const minimumValue = packageItem.extra.whole_group
        ? bookingFilter.adults + bookingFilter.children
        : competitionUpsell
          ? 1
          : packageItem.minimum_persons_count
    const maximumValue = packageItem.maximum_persons_count

    const [count, setCount] = useState<number>(() => {
        return selectedUpsell ? selectedUpsell.count : computeInitialCount(maximumValue)
    })
    const [customText, setCustomText] = useState('')

    const applyCount = (value: number) => {
        const safeValue = Math.min(Math.max(value, minimumValue), maximumValue)
        setCount(safeValue)
    }

    const handleDecrement = () => {
        const next = count - 1
        if (next < minimumValue) {
            if (minimumValue > 0) {
                toast.error(t('cannot order less', { minimum_value: minimumValue }), { toastId: 'cannotOrderless' })
            }
            return
        }
        applyCount(next)
    }

    const handleIncrement = () => {
        const next = count + 1
        if (next > maximumValue) {
            toast.warning(t('We cannot take more people than: ') + maximumValue, { toastId: 'maximumDriverCount' })
            return
        }
        applyCount(next)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const parsed = Number(e.target.value.trim())
        if (isNaN(parsed) || parsed < minimumValue) {
            applyCount(minimumValue)
            return
        }
        applyCount(Math.min(parsed, maximumValue))
    }

    const handleConfirmAdd = () => {
        const resolvedCount = packageItem.extra.whole_group ? bookingFilter.adults + bookingFilter.children : count
        const payload = { packageItem, upsell_option: 0, count: resolvedCount }

        if (isSelected) {
            dispatch(BookingAction.setUpsell(payload))
        } else {
            dispatch(BookingAction.pushUpsell(payload))
        }
        setIsSlideOverOpen(false)
    }

    const handleRemove = () => {
        const currentUpsell = getSelectedUpsell(bookingProcess.upsell, packageItem)
        if (currentUpsell) {
            dispatch(BookingAction.removeUpsell(currentUpsell))
        }
        setIsSlideOverOpen(false)
    }

    const displayImage = props.item.image || packageItem.image
    const displayName = translate(props.item.name)
    const displayDescription =  translate(props.item.custom_text || props.item.description_html)

    useEffect(() => {
        if (!isSlideOverOpen) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsSlideOverOpen(false)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [isSlideOverOpen])

    useEffect(() => {
        if (isSelected && selectedCount !== count) {
            setCount(selectedCount)
        }
    }, [isSelected, selectedCount])

    useEffect(() => {
        if (!isSelected) {
            setCount(computeInitialCount(maximumValue))
        }
    }, [bookingFilter.adults, bookingFilter.children])

    useEffect(() => {
        const normalizedCustomText = toCustomTextValue(customText)

        if (!isSlideOverOpen || normalizedCustomText.trim().length > 0) {
            return
        }

        setCustomText(toCustomTextValue(displayDescription || displayName))
    }, [customText, displayDescription, displayName, isSlideOverOpen])

    return (
        <>
            <PackageUpsellSelectableRow
                descriptionHtml={displayDescription}
                imageSrc={displayImage}
                isCompact={props.isCompact}
                maxBadge={
                    packageItem.maximum_persons_count > 0 && packageItem.maximum_persons_count < 1000 ? (
                        <UiPill className="col-start-2 text-center bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 group-hover:bg-white dark:group-hover:bg-slate-700">
                            <span>{t('Max')} {packageItem.maximum_persons_count}</span>
                        </UiPill>
                    ) : null
                }
                minBadge={
                    packageItem.minimum_persons_count > 0 ? (
                        <UiPill className="col-start-1 text-center bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 group-hover:bg-white dark:group-hover:bg-slate-700">
                            <span>{t('Min')} {packageItem.minimum_persons_count}</span>
                        </UiPill>
                    ) : null
                }
                onClick={() => setIsSlideOverOpen(true)}
                price={currencyNumberFormat(packageItem.price)}
                selected={isSelected}
                statusBadge={
                    isSelected ? (
                        <div className="rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white col-span-3 place-self-end">
                            ✓ {count}× {t('Selected')}
                        </div>
                    ) : null
                }
                title={displayName}
            />

            <PackageUpsellSlideOver
                actionsSlot={
                    isSelected ? (
                        <>
                            <button
                                className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-blue-700 transition"
                                type="button"
                                onClick={handleConfirmAdd}
                            >
                                {t('Update')}
                            </button>
                            <button
                                className="rounded-2xl bg-red-50 dark:bg-red-900/30 px-5 py-3 text-sm font-bold text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition"
                                type="button"
                                onClick={handleRemove}
                            >
                                {t('Remove')}
                            </button>
                        </>
                    ) : (
                        <button
                            className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-blue-700 transition"
                            type="button"
                            onClick={handleConfirmAdd}
                        >
                            {t('Add')}
                        </button>
                    )
                }
                customTextSlot={
                    <UiRoundedContent className={"UpsellPricelistSimpleRow"}>
                        <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                            <p className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                {t('Custom Text')}
                            </p>
                            <button
                                className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                type="button"
                                onClick={() =>
                                    setCustomText(
                                        toCustomTextValue(
                                            displayDescription || displayName
                                        )
                                    )
                                }
                            >
                                {t('Auto fill')}
                            </button>
                        </div>
                        <div className="p-3">
                            <textarea
                                className="w-full rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 shadow-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900"
                                placeholder={t('Enter custom text...')}
                                rows={3}
                                value={customText}
                                onChange={(e) => setCustomText(e.target.value)}
                            />
                        </div>
                    </UiRoundedContent>
                }
                descriptionHtml={displayDescription}
                detailsSlot={
                    <UiRoundedContent className={"UplsellPricelistSimpleRowTwo"}>
                        <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-700">
                            <p className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                {t('Item Details')}
                            </p>
                        </div>
                        <div className="p-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div className="sm:col-span-1">
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                    {t('Quantity')}
                                </span>
                                <div className="mt-2 flex items-center gap-2">
                                    <button
                                        className="size-10 flex items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition active:scale-95 text-xl font-bold"
                                        type="button"
                                        onClick={handleDecrement}
                                    >
                                        −
                                    </button>
                                    <input
                                        className="min-w-0 flex-1 rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-800 px-3 py-2 text-center text-base font-bold text-slate-950 dark:text-slate-100 shadow-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900"
                                        inputMode="numeric"
                                        max={maximumValue}
                                        min={minimumValue}
                                        type="text"
                                        value={count}
                                        onChange={(e) => {
                                            const val = Number(e.target.value)
                                            if (!isNaN(val)) setCount(val)
                                        }}
                                        onBlur={handleBlur}
                                    />
                                    <button
                                        className="size-10 flex items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition active:scale-95 text-xl font-bold"
                                        type="button"
                                        onClick={handleIncrement}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            <div className="sm:col-span-1">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                    {t('Unit Price')}
                                </label>
                                <input
                                    className="mt-2 w-full rounded-2xl border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base font-semibold text-slate-700 dark:text-slate-200"
                                    disabled
                                    type="text"
                                    value={currencyNumberFormat(packageItem.price)}
                                />
                            </div>
                            <div className="sm:col-span-1">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                    {t('Total Price')}
                                </label>
                                <input
                                    className="mt-2 w-full rounded-2xl border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base font-semibold text-slate-700 dark:text-slate-200"
                                    disabled
                                    type="text"
                                    value={currencyNumberFormat(packageItem.price * count)}
                                />
                            </div>
                        </div>
                    </UiRoundedContent>
                }
                groupLabel={packageItem.extra?.groupName}
                imageSrc={displayImage}
                inCartCount={selectedCount}
                isOpen={isSlideOverOpen}
                onClose={() => setIsSlideOverOpen(false)}
                title={displayName}
                totalPrice={currencyNumberFormat(packageItem.price * count)}
                unitPrice={currencyNumberFormat(packageItem.price)}
            />
        </>
    )
}

export default UpsellPriceListSimpleRow
